import { useMutation } from "@tanstack/react-query";

import { apiClient } from "@/api/client";

import type { MarketplaceId } from "../lib/types";

export interface ConnectResult {
  authUrl: string;
  /** The shop this connect would disconnect, or null when nothing is active. */
  currentActiveShop: { shop_id: string; shop_name: string } | null;
}

/**
 * POST /v1/marketplace/{marketplace}/connect — returns the OAuth authorization
 * URL and the shop currently active on that marketplace.
 *
 * It deliberately does NOT navigate. Connecting a new shop disconnects the
 * active one (BE spec D2), so when `currentActiveShop` is set the page has to
 * confirm with the operator first; redirecting from inside the mutation would
 * make that impossible.
 */
export function useConnect() {
  return useMutation({
    meta: { silentError: true },
    mutationFn: async (marketplace: MarketplaceId): Promise<ConnectResult> => {
      const { data, error } = await apiClient.POST("/v1/marketplace/{marketplace}/connect", {
        params: { path: { marketplace } },
      });
      if (error) throw error;
      return { authUrl: data.auth_url, currentActiveShop: data.current_active_shop ?? null };
    },
  });
}
